import type { Property, Unit, Tenant, Lease, LeaseStatus, LeaseType, RentPayment, PaymentAllocation, LateFee, Expense, Income, MaintenanceRequest, PortalPayment, Handyman, UtilityAccount, Appliance, CalendarEvent, LeaseAuditEntry, LeaseNotification, PropertyProfile, PropertyNote, NoteAttachment, ExpenseImport, ExpenseImportDetail, CapitalProject, CapitalProjectDetail, DepositReturn, Inspection, Notice, Task, TaskComment, TaskActivityEntry, Project, Eviction, EvictionCost } from '../types';

const API_BASE = '/api';

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const url = `${API_BASE}${endpoint}`;
  
  // FormData requests must NOT set Content-Type (the browser adds it with the
  // multipart boundary). Every other request is JSON.
  const isFormData = options.body instanceof FormData;
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: isFormData
      ? { ...options.headers }
      : { 'Content-Type': 'application/json', ...options.headers },
  });
  
  if (response.status === 401) {
    // Don't redirect automatically - let the component handle it
    throw new Error('Unauthorized');
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  
  const data = await response.json();
  
  // If the response has a data property, return that (API format)
  // Otherwise return the whole response (for custom endpoints)
  return data.data !== undefined ? data.data : data;
}

// Auth API
export const authApi = {
  signUp: (email: string, password: string, name: string) =>
    apiRequest('/auth/sign-up/email', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    }),
  
  signIn: (email: string, password: string, code?: string, rememberDevice?: boolean) =>
    apiRequest('/auth/sign-in/email', {
      method: 'POST',
      body: JSON.stringify({ email, password, code, rememberDevice }),
    }),

  // Two-factor authentication (TOTP) for the signed-in user's own account.
  twoFactorSetup: (): Promise<{ secret: string; otpauthUrl: string }> =>
    apiRequest('/auth/2fa/setup', { method: 'POST' }),
  twoFactorEnable: (code: string): Promise<{ backupCodes: string[] }> =>
    apiRequest('/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) }),
  twoFactorDisable: (code: string): Promise<{ success: boolean }> =>
    apiRequest('/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) }),
  
  signOut: () =>
    apiRequest('/auth/sign-out', { method: 'POST' }),
  
  getSession: () =>
    apiRequest('/auth/session'),

  // Self-service profile: any signed-in user edits their own name, phone, photo.
  updateMe: (data: { firstName: string; lastName?: string; phone?: string }): Promise<{ firstName: string; lastName: string; phone?: string }> =>
    apiRequest('/me', { method: 'PUT', body: JSON.stringify(data) }),
  uploadMyPhoto: (file: Blob): Promise<{ photoUrl: string }> => postPhoto('/me/photo', file),
  removeMyPhoto: (): Promise<{ success: boolean }> => apiRequest('/me/photo', { method: 'DELETE' }),

  forgotPassword: (email: string) =>
    apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  
  resetPassword: (userId: string, newPassword: string, currentPassword?: string) =>
    apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ userId, newPassword, currentPassword }),
    }),

  resetPasswordWithToken: (token: string, newPassword: string) =>
    apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),
};

// Admin (users) API
export interface ApiUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  department?: string;
  birthdate?: string;
  roleId: string;
  isActive: boolean;
  createdAt: string;
  photoUrl?: string | null;
  companyName?: string;
}

export const adminApi = {
  listUsers: (): Promise<ApiUser[]> => apiRequest('/admin/users'),
  createUser: (data: {
    firstName: string; lastName: string; email: string; roleId: string;
    phone?: string; department?: string; birthdate?: string;
  }): Promise<{ success: boolean; user: unknown; emailed?: boolean; tempPassword?: string; message?: string }> =>
    apiRequest('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id: string, data: {
    firstName: string; lastName: string; phone?: string; department?: string;
    birthdate?: string; isActive: boolean; roleId: string;
  }): Promise<ApiUser> =>
    apiRequest(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id: string) =>
    apiRequest(`/admin/users/${id}`, { method: 'DELETE' }),
  resetUserPassword: (userId: string): Promise<{ tempPassword: string; message: string }> =>
    apiRequest('/admin/reset-user-password', { method: 'POST', body: JSON.stringify({ userId }) }),
  // One-time: nest documents into Property/Unit -> Tenant folders.
  migrateDocFolders: (): Promise<{ moved: number; foldersNested: number }> =>
    apiRequest('/admin/migrate-doc-folders', { method: 'POST' }),
  // Per-user permission overrides
  getUserPermissions: (userId: string): Promise<{ permission: string; grantedBy: string; grantedAt: number }[]> =>
    apiRequest(`/admin/users/${userId}/permissions`),
  grantPermissions: (userId: string, permissions: string[]): Promise<{ granted: number }> =>
    apiRequest(`/admin/users/${userId}/permissions`, { method: 'POST', body: JSON.stringify({ permissions }) }),
  revokePermissions: (userId: string, permissions: string[]): Promise<{ revoked: number }> =>
    apiRequest(`/admin/users/${userId}/permissions`, { method: 'DELETE', body: JSON.stringify({ permissions }) }),
  // Permission audit log
  getPermissionAudit: (params?: { userId?: string; limit?: number; offset?: number }): Promise<{
    id: string; targetUserId: string; targetName: string; targetEmail: string;
    changedById: string; changedByName: string; action: string;
    permission?: string; oldRole?: string; newRole?: string; createdAt: number;
  }[]> => {
    const search = new URLSearchParams();
    if (params?.userId) search.set('userId', params.userId);
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.offset) search.set('offset', String(params.offset));
    const qs = search.toString();
    return apiRequest(`/admin/permission-audit${qs ? '?' + qs : ''}`);
  },
};

// Roles API
export interface ApiRole {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isSystem: boolean;
}

export const rolesApi = {
  getAll: (): Promise<ApiRole[]> => apiRequest('/roles'),
  create: (data: { name: string; description: string; permissions: string[] }): Promise<ApiRole> =>
    apiRequest('/roles', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: { name?: string; description?: string; permissions: string[] }): Promise<ApiRole> =>
    apiRequest(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/roles/${id}`, { method: 'DELETE' }),
};

// Settings API
export interface AppSettings {
  company: {
    companyName: string; address: string; city: string; state: string;
    zipCode: string; phone: string; email: string; taxId: string;
  };
  rent: {
    lateFeeAmount: number; lateFeeDay: number; gracePeriod: number;
    defaultLeaseTerm: number; securityDepositMultiplier: number;
    paymentInstructions: string; pastDueMonths: number; rentDueDay: number;
  };
  notifications: {
    emailNotifications: boolean; smsNotifications: boolean; rentReminders: boolean;
    leaseExpiryAlerts: boolean; maintenanceAlerts: boolean; paymentConfirmations: boolean;
  };
  finances: {
    capitalThreshold: number;
  };
}

export const settingsApi = {
  get: (): Promise<AppSettings> => apiRequest('/settings'),
  update: (data: Partial<AppSettings>): Promise<AppSettings> =>
    apiRequest('/settings', { method: 'PUT', body: JSON.stringify(data) }),
};

// Properties API
export const propertiesApi = {
  getAll: (): Promise<Property[]> => apiRequest('/properties'),
  getById: (id: string): Promise<Property> => apiRequest(`/properties/${id}`),
  create: (data: Omit<Property, 'id'>): Promise<Property> =>
    apiRequest('/properties', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Property): Promise<Property> =>
    apiRequest(`/properties/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/properties/${id}`, { method: 'DELETE' }),
  getProfile: (id: string): Promise<PropertyProfile> =>
    apiRequest(`/properties/${id}/profile`),
  uploadPhoto: (id: string, file: File): Promise<{ image: string }> => {
    const form = new FormData();
    form.append('photo', file);
    return apiRequest(`/properties/${id}/photo`, { method: 'POST', body: form });
  },
  removePhoto: (id: string) =>
    apiRequest(`/properties/${id}/photo`, { method: 'DELETE' }),
};

// Property Notes API
export const propertyNotesApi = {
  list: (propertyId: string, opts?: { category?: string; search?: string }): Promise<PropertyNote[]> => {
    const params = new URLSearchParams();
    if (opts?.category) params.set('category', opts.category);
    if (opts?.search) params.set('search', opts.search);
    const qs = params.toString();
    return apiRequest(`/properties/${propertyId}/notes${qs ? `?${qs}` : ''}`);
  },
  create: (propertyId: string, data: { title: string; content: string; category: string; isPinned?: boolean }): Promise<PropertyNote> =>
    apiRequest(`/properties/${propertyId}/notes`, { method: 'POST', body: JSON.stringify(data) }),
  update: (propertyId: string, noteId: string, data: { title?: string; content?: string; category?: string; isPinned?: boolean }): Promise<PropertyNote> =>
    apiRequest(`/properties/${propertyId}/notes/${noteId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (propertyId: string, noteId: string) =>
    apiRequest(`/properties/${propertyId}/notes/${noteId}`, { method: 'DELETE' }),
  togglePin: (propertyId: string, noteId: string): Promise<{ isPinned: boolean }> =>
    apiRequest(`/properties/${propertyId}/notes/${noteId}/pin`, { method: 'POST' }),
  uploadAttachment: async (propertyId: string, noteId: string, file: File): Promise<NoteAttachment> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/properties/${propertyId}/notes/${noteId}/attachments`, {
      method: 'POST', body: fd, credentials: 'include',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const json = await res.json();
    return json.data !== undefined ? json.data : json;
  },
  deleteAttachment: (propertyId: string, noteId: string, attachmentId: string) =>
    apiRequest(`/properties/${propertyId}/notes/${noteId}/attachments?attachmentId=${attachmentId}`, { method: 'DELETE' }),
};

// Utility accounts API (water/gas/electric the landlord pays, per property)
export const utilityAccountsApi = {
  getAll: (): Promise<UtilityAccount[]> => apiRequest('/utility-accounts'),
  create: (data: Omit<UtilityAccount, 'id'>): Promise<UtilityAccount> =>
    apiRequest('/utility-accounts', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UtilityAccount): Promise<UtilityAccount> =>
    apiRequest(`/utility-accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/utility-accounts/${id}`, { method: 'DELETE' }),
};

// Appliances API (major appliances per property/unit with warranty tracking)
export const appliancesApi = {
  getAll: (propertyId?: string): Promise<Appliance[]> =>
    apiRequest(propertyId ? `/appliances?propertyId=${propertyId}` : '/appliances'),
  create: (data: Omit<Appliance, 'id'>): Promise<Appliance> =>
    apiRequest('/appliances', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Appliance): Promise<Appliance> =>
    apiRequest(`/appliances/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/appliances/${id}`, { method: 'DELETE' }),
};

// Units API
export const unitsApi = {
  getAll: (): Promise<Unit[]> => apiRequest('/units'),
  getById: (id: string): Promise<Unit> => apiRequest(`/units/${id}`),
  create: (data: Omit<Unit, 'id'>): Promise<Unit> =>
    apiRequest('/units', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Unit): Promise<Unit> =>
    apiRequest(`/units/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/units/${id}`, { method: 'DELETE' }),
};

// A realtor linked to a tenant, as GET /api/tenants/:id/realtors returns it.
export interface TenantRealtorLink {
  id: string;
  realtorUserId: string;
  name: string;
  email: string;
  linkedOn: string;
  accessEndsOn: string;
}

// Response of POST /api/tenants/:id/invite. When mail isn't configured,
// `inviteUrl` carries the set-password link so Belle can pass it on by hand.
export interface InviteResult {
  emailSent: boolean;
  inviteUrl?: string;
  // True when this re-sent a fresh link to an existing login (vs a first invite).
  resent?: boolean;
}

// Tenants API
/** One logged outbound email from the office to a tenant. createdAt is unix seconds. */
export interface TenantEmail {
  id: string;
  tenantId: string;
  sentByUserId?: string;
  toEmail: string;
  subject: string;
  body: string;
  delivered: boolean;
  createdAt: number;
}

export const tenantsApi = {
  getAll: (): Promise<Tenant[]> => apiRequest('/tenants'),
  getById: (id: string): Promise<Tenant> => apiRequest(`/tenants/${id}`),
  create: (data: Omit<Tenant, 'id'>): Promise<Tenant> =>
    apiRequest('/tenants', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Tenant): Promise<Tenant> =>
    apiRequest(`/tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/tenants/${id}`, { method: 'DELETE' }),
  // Gives the tenant a portal login. 400s with "This tenant already has a
  // login" (or "no email address") when it can't — there is no field on the
  // tenant that says this up front, so the caller shows that message as-is.
  invite: (id: string): Promise<InviteResult> =>
    apiRequest(`/tenants/${id}/invite`, { method: 'POST' }),
  // Reset the tenant's portal login password; returns a one-time temp password
  // to share. 400s with a message if they have no login yet.
  resetPassword: (id: string): Promise<{ tempPassword: string; message: string }> =>
    apiRequest(`/tenants/${id}/reset-password`, { method: 'POST' }),
  // Outbound email to the tenant + the office correspondence log (backend only).
  emails: (id: string): Promise<TenantEmail[]> => apiRequest(`/tenants/${id}/emails`),
  sendEmail: (id: string, subject: string, body: string): Promise<TenantEmail> =>
    apiRequest(`/tenants/${id}/emails`, { method: 'POST', body: JSON.stringify({ subject, body }) }),
  getRealtors: (id: string): Promise<TenantRealtorLink[]> =>
    apiRequest(`/tenants/${id}/realtors`),
  linkRealtor: (id: string, realtorUserId: string) =>
    apiRequest(`/tenants/${id}/realtors`, { method: 'POST', body: JSON.stringify({ realtorUserId }) }),
  // Tenants not linked to any realtor yet, for the admin "link existing" picker.
  listUnlinked: async (): Promise<{ id: string; firstName: string; lastName: string }[]> => {
    const rows = (await apiRequest('/tenants?withoutRealtor=1')) as Tenant[];
    return rows.map(t => ({ id: t.id, firstName: t.firstName, lastName: t.lastName }));
  },
  unlinkRealtor: (id: string, realtorUserId: string) =>
    apiRequest(`/tenants/${id}/realtors?realtorUserId=${encodeURIComponent(realtorUserId)}`, { method: 'DELETE' }),
  // The realtor-role users, for the link picker. Gated on tenants_edit server
  // side, so it works for any staff member who can link a realtor, not only
  // those who also hold users_view.
  listRealtorUsers: (): Promise<RealtorUserOption[]> => apiRequest('/realtors'),
  // Tenant credit balance ledger.
  getCredits: (id: string): Promise<TenantCreditsResponse> =>
    apiRequest(`/tenants/${id}/credits`),
  addCredit: (id: string, data: { amount: number; reason?: string; notes?: string }): Promise<{ id: string; balance: number }> =>
    apiRequest(`/tenants/${id}/credits`, { method: 'POST', body: JSON.stringify(data) }),
  updateCredit: (tenantId: string, creditId: string, data: { amount?: number; reason?: string; notes?: string }): Promise<{ success: boolean; balance: number }> =>
    apiRequest(`/tenants/${tenantId}/credits/${creditId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCredit: (tenantId: string, creditId: string): Promise<{ success: boolean; balance: number }> =>
    apiRequest(`/tenants/${tenantId}/credits/${creditId}`, { method: 'DELETE' }),
};

export interface TenantNote {
  id: string;
  tenantId: string;
  body: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number | null;
  authorName: string | null;
}

export const tenantNotesApi = {
  list: (tenantId: string): Promise<TenantNote[]> =>
    apiRequest(`/tenants/${tenantId}/notes`),
  add: (tenantId: string, body: string): Promise<TenantNote> =>
    apiRequest(`/tenants/${tenantId}/notes`, { method: 'POST', body: JSON.stringify({ body }) }),
  update: (tenantId: string, noteId: string, body: string): Promise<{ success: boolean }> =>
    apiRequest(`/tenants/${tenantId}/notes`, { method: 'PUT', body: JSON.stringify({ noteId, body }) }),
  remove: (tenantId: string, noteId: string): Promise<{ success: boolean }> =>
    apiRequest(`/tenants/${tenantId}/notes`, { method: 'DELETE', body: JSON.stringify({ noteId }) }),
};

export interface TenantCreditEntry {
  id: string;
  tenantId: string;
  amount: number;
  reason: string | null;
  notes: string | null;
  appliedToPaymentId: string | null;
  createdBy: string;
  createdAt: number;
}

export interface TenantCreditsResponse {
  balance: number;
  entries: TenantCreditEntry[];
}

export interface RealtorUserOption {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  company_name?: string | null;
}

// Realtors API (staff side: acting on a realtor's behalf).
export const realtorsApi = {
  // Staff create a brand new tenant and link it to this realtor in one step.
  addTenant: (realtorUserId: string, data: { firstName: string; lastName: string; email?: string; phone?: string }): Promise<Tenant> =>
    apiRequest(`/realtors/${realtorUserId}/tenants`, { method: 'POST', body: JSON.stringify(data) }),
  // Add a realtor: creates their portal login with the realtor role and invites them.
  create: (data: { firstName: string; lastName: string; email: string; phone?: string; companyName?: string }): Promise<{ userId: string; emailSent: boolean; inviteUrl?: string }> =>
    apiRequest('/realtors', { method: 'POST', body: JSON.stringify(data) }),
  // Resend the set-password invite to an existing realtor.
  resendInvite: (userId: string): Promise<{ emailSent: boolean; inviteUrl?: string }> =>
    apiRequest(`/realtors/${userId}/invite`, { method: 'POST' }),
  // Edit a realtor's own details (never changes their role).
  update: (userId: string, data: { firstName: string; lastName: string; email: string; phone?: string; companyName?: string }): Promise<unknown> =>
    apiRequest(`/realtors/${userId}`, { method: 'PUT', body: JSON.stringify(data) }),
  payCommission: (userId: string, data: { amount: number; description: string; propertyId?: string; unitId?: string; paymentMethod?: string }): Promise<{ expenseId: string }> =>
    apiRequest(`/realtors/${userId}/pay-commission`, { method: 'POST', body: JSON.stringify(data) }),
};

// Household API (admin side: household members for a given tenant's lease).
// Mirrors portalApi.household, but scoped by tenantId and gated on
// tenants_edit server side, since only staff with edit rights should be able
// to add, change, or remove someone's household.
export const householdApi = {
  list: (tenantId: string): Promise<HouseholdMember[]> =>
    apiRequest(`/household?tenantId=${encodeURIComponent(tenantId)}`),
  add: (tenantId: string, data: HouseholdInput): Promise<HouseholdMember> =>
    apiRequest('/household', { method: 'POST', body: JSON.stringify({ tenantId, ...data }) }),
  update: (id: string, data: HouseholdInput): Promise<HouseholdMember> =>
    apiRequest(`/household/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string): Promise<{ success: boolean }> =>
    apiRequest(`/household/${id}`, { method: 'DELETE' }),
};

// Leases API
export const leasesApi = {
  getAll: (): Promise<Lease[]> => apiRequest('/leases'),
  getById: (id: string): Promise<Lease> => apiRequest(`/leases/${id}`),
  // `pausedAt` is not part of the Lease shape (the server owns `pauses` and
  // stamps it from status transitions); it exists only so the CSV importer
  // can ask the POST to open an interval for an imported paused lease.
  create: (data: Omit<Lease, 'id'> & { pausedAt?: string }): Promise<Lease> =>
    apiRequest('/leases', { method: 'POST', body: JSON.stringify(data) }),
  // `statusChangedOn` (YYYY-MM-DD, the OWNER's local day) tells the server
  // which day to stamp a pause/resume interval on, since the server has no
  // timezone of its own to derive "today" from.
  update: (id: string, data: Lease & { statusChangedOn?: string }): Promise<Lease> =>
    apiRequest(`/leases/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest(`/leases/${id}`, { method: 'DELETE' }),
  recordMoveInFee: (id: string, data: { amount: number; paidDate: string; method?: string }): Promise<{ moveInFeePaid: boolean; moveInFeePaidDate: string; moveInFeeMethod?: string; securityDeposit: number; receiptDocumentId: string | null }> =>
    apiRequest(`/leases/${id}/move-in-fee`, { method: 'POST', body: JSON.stringify(data) }),
  editMoveInFee: (id: string, data: { paidDate?: string; amount?: number; method?: string; reason?: string }): Promise<{ success: boolean }> =>
    apiRequest(`/leases/${id}/move-in-fee`, { method: 'PUT', body: JSON.stringify(data) }),
  approveRenewal: (id: string, action: 'approve' | 'reject'): Promise<{ status: string; newLeaseId?: string }> =>
    apiRequest(`/leases/${id}/approve-renewal`, { method: 'POST', body: JSON.stringify({ action }) }),
  getAuditHistory: (id: string): Promise<LeaseAuditEntry[]> =>
    apiRequest(`/leases/${id}/audit`),
  getNotifications: (id: string): Promise<LeaseNotification[]> =>
    apiRequest(`/leases/${id}/notifications`),
  resendNotification: (id: string): Promise<void> =>
    apiRequest(`/leases/${id}/notifications`, { method: 'POST' }),
};

// Payments API
export interface PaymentCreateData extends Omit<RentPayment, 'id'> {
  debitCreditBalance?: boolean;
  allocations?: Array<{ month: number; year: number; amount: number; type?: 'rent' | 'late_fee' }>;
}

export interface PaymentUpdateData extends RentPayment {
  allocations?: Array<{ month: number; year: number; amount: number; type?: 'rent' | 'late_fee' }>;
}

export const paymentsApi = {
  getAll: (): Promise<RentPayment[]> => apiRequest('/payments'),
  getById: (id: string): Promise<RentPayment & { allocations?: PaymentAllocation[] }> => apiRequest(`/payments/${id}`),
  // deferSheetSync lets a bulk import skip the per-row master-spreadsheet
  // rebuild and trigger a single rebuild once, at the end.
  create: (data: PaymentCreateData, opts?: { deferSheetSync?: boolean }): Promise<RentPayment & { allocations?: PaymentAllocation[] }> =>
    apiRequest(`/payments${opts?.deferSheetSync ? '?deferSheetSync=1' : ''}`, { method: 'POST', body: JSON.stringify(data) }),
  // Generate (or refresh) the PDF receipt for a paid payment; returns its
  // document id so the UI can link to the download.
  generateReceipt: (id: string): Promise<{ receiptDocumentId: string }> =>
    apiRequest(`/payments/${id}/receipt`, { method: 'POST' }),
  update: (id: string, data: PaymentUpdateData): Promise<RentPayment & { allocations?: PaymentAllocation[] }> =>
    apiRequest(`/payments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/payments/${id}`, { method: 'DELETE' }),
  sendPaymentLink: (data: { tenantId: string; leaseId: string; amount: number; month: number; year: number; note?: string; email?: string }): Promise<{ sent: boolean; url: string; email: string; amount: number; fee: number; total: number }> =>
    apiRequest('/admin/send-payment-link', { method: 'POST', body: JSON.stringify(data) }),
};

// Allocations API
export const allocationsApi = {
  getAll: (): Promise<PaymentAllocation[]> => apiRequest('/allocations'),
};

// Late Fees API
export const lateFeesApi = {
  getAll: (leaseId?: string): Promise<LateFee[]> =>
    apiRequest(`/late-fees${leaseId ? `?leaseId=${leaseId}` : ''}`),
  getById: (id: string): Promise<LateFee> => apiRequest(`/late-fees/${id}`),
  create: (data: { leaseId: string; month: number; year: number; amount: number; assessedDate: string; tenantId?: string; notes?: string }): Promise<LateFee> =>
    apiRequest('/late-fees', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<LateFee>): Promise<LateFee> =>
    apiRequest(`/late-fees/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  waive: (id: string, waiveReason?: string): Promise<LateFee> =>
    apiRequest(`/late-fees/${id}`, { method: 'PUT', body: JSON.stringify({ action: 'waive', waiveReason }) }),
  delete: (id: string) =>
    apiRequest(`/late-fees/${id}`, { method: 'DELETE' }),
};

// Expenses API
export const expensesApi = {
  getAll: (): Promise<Expense[]> => apiRequest('/expenses'),
  getById: (id: string): Promise<Expense> => apiRequest(`/expenses/${id}`),
  create: (data: Omit<Expense, 'id'>): Promise<Expense> =>
    apiRequest('/expenses', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Expense): Promise<Expense> =>
    apiRequest(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/expenses/${id}`, { method: 'DELETE' }),
  // Upload a receipt image for an expense; stored in the unit's Drive folder.
  uploadReceipt: async (id: string, file: File): Promise<Expense> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/expenses/${id}/receipt`, { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.data !== undefined ? data.data : data;
  },
};

// Incomes API
export const incomesApi = {
  getAll: (): Promise<Income[]> => apiRequest('/incomes'),
  getById: (id: string): Promise<Income> => apiRequest(`/incomes/${id}`),
  create: (data: Omit<Income, 'id'>): Promise<Income> =>
    apiRequest('/incomes', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Income): Promise<Income> =>
    apiRequest(`/incomes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/incomes/${id}`, { method: 'DELETE' }),
};

// Deposit Returns API (move-out security deposit accounting)
export const depositReturnsApi = {
  getAll: (filters?: { leaseId?: string; tenantId?: string; status?: string }): Promise<DepositReturn[]> => {
    const params = new URLSearchParams();
    if (filters?.leaseId) params.set('leaseId', filters.leaseId);
    if (filters?.tenantId) params.set('tenantId', filters.tenantId);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    return apiRequest(`/deposit-returns${qs ? `?${qs}` : ''}`);
  },
  getById: (id: string): Promise<DepositReturn> => apiRequest(`/deposit-returns/${id}`),
  create: (data: {
    leaseId: string;
    tenantId?: string;
    propertyId?: string;
    unitId?: string;
    depositAmount: number;
    moveOutDate: string;
    deadlineDate?: string;
    notes?: string;
  }): Promise<DepositReturn> =>
    apiRequest('/deposit-returns', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: {
    status?: string;
    moveOutDate?: string;
    deadlineDate?: string;
    depositAmount?: number;
    refundDate?: string;
    refundMethod?: string;
    notes?: string;
    deductions?: Array<{ category: string; description: string; amount: number }>;
  }): Promise<DepositReturn> =>
    apiRequest(`/deposit-returns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/deposit-returns/${id}`, { method: 'DELETE' }),
};

// Inspections API (move-in / move-out / periodic property inspections)
export const inspectionsApi = {
  getAll: (filters?: { unitId?: string; leaseId?: string; tenantId?: string; type?: string }): Promise<Inspection[]> => {
    const params = new URLSearchParams();
    if (filters?.unitId) params.set('unitId', filters.unitId);
    if (filters?.leaseId) params.set('leaseId', filters.leaseId);
    if (filters?.tenantId) params.set('tenantId', filters.tenantId);
    if (filters?.type) params.set('type', filters.type);
    const qs = params.toString();
    return apiRequest(`/inspections${qs ? `?${qs}` : ''}`);
  },
  getById: (id: string): Promise<Inspection> => apiRequest(`/inspections/${id}`),
  create: (data: {
    propertyId?: string;
    unitId?: string;
    leaseId?: string;
    tenantId?: string;
    type: string;
    inspectionDate: string;
    inspectorName?: string;
    status?: string;
    notes?: string;
    items?: Array<{ room: string; item: string; condition: string; notes?: string }>;
  }): Promise<Inspection> =>
    apiRequest('/inspections', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: {
    type?: string;
    inspectionDate?: string;
    inspectorName?: string;
    status?: string;
    notes?: string;
    driveFileId?: string;
    items?: Array<{ room: string; item: string; condition: string; notes?: string }>;
  }): Promise<Inspection> =>
    apiRequest(`/inspections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/inspections/${id}`, { method: 'DELETE' }),
};

// Notices API (tenant notices and letters)
export const noticesApi = {
  getAll: (filters?: { tenantId?: string; leaseId?: string; type?: string; status?: string }): Promise<Notice[]> => {
    const params = new URLSearchParams();
    if (filters?.tenantId) params.set('tenantId', filters.tenantId);
    if (filters?.leaseId) params.set('leaseId', filters.leaseId);
    if (filters?.type) params.set('type', filters.type);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    return apiRequest(`/notices${qs ? `?${qs}` : ''}`);
  },
  getById: (id: string): Promise<Notice> => apiRequest(`/notices/${id}`),
  create: (data: {
    propertyId?: string;
    unitId?: string;
    leaseId?: string;
    tenantId?: string;
    type: string;
    title: string;
    body: string;
    noticeDate: string;
    deliveryMethod?: string;
    status?: string;
  }): Promise<Notice> =>
    apiRequest('/notices', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: {
    type?: string;
    title?: string;
    body?: string;
    noticeDate?: string;
    deliveryMethod?: string;
    deliveredAt?: string;
    status?: string;
    driveFileId?: string;
  }): Promise<Notice> =>
    apiRequest(`/notices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/notices/${id}`, { method: 'DELETE' }),
  createBulk: (data: {
    notices: Array<{
      propertyId?: string;
      unitId?: string;
      leaseId?: string;
      tenantId?: string;
      tenantEmail?: string;
      type: string;
      title: string;
      body: string;
      noticeDate: string;
      deliveryMethod?: string;
      status?: string;
      monthsBehind?: number;
      totalOwed?: string;
      tenantName?: string;
      propertyAddress?: string;
      unitNumber?: string;
    }>;
    sendEmail?: boolean;
  }): Promise<{ created: number; emailed: boolean }> =>
    apiRequest('/notices/bulk', { method: 'POST', body: JSON.stringify(data) }),
};

// Evictions API
export const evictionsApi = {
  getAll: (filters?: { tenantId?: string; status?: string }): Promise<Eviction[]> => {
    const params = new URLSearchParams();
    if (filters?.tenantId) params.set('tenantId', filters.tenantId);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    return apiRequest(`/evictions${qs ? `?${qs}` : ''}`);
  },
  getById: (id: string): Promise<Eviction & { costs: EvictionCost[] }> =>
    apiRequest(`/evictions/${id}`),
  create: (data: {
    tenantId: string;
    leaseId?: string;
    propertyId?: string;
    unitId?: string;
    status?: string;
    reason: string;
    reasonNotes?: string;
    noticeServedDate?: string;
    filedDate?: string;
    courtDate?: string;
    lawyerName?: string;
    lawyerPhone?: string;
    caseNumber?: string;
    courtName?: string;
    notes?: string;
  }): Promise<{ id: string }> =>
    apiRequest('/evictions', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>): Promise<void> =>
    apiRequest(`/evictions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string): Promise<void> =>
    apiRequest(`/evictions/${id}`, { method: 'DELETE' }),
  getCosts: (evictionId: string): Promise<EvictionCost[]> =>
    apiRequest(`/evictions/${evictionId}/costs`),
  addCost: (evictionId: string, data: {
    description: string;
    amount: number;
    costDate: string;
    category?: string;
    notes?: string;
  }): Promise<{ id: string }> =>
    apiRequest(`/evictions/${evictionId}/costs`, { method: 'POST', body: JSON.stringify(data) }),
  deleteCost: (evictionId: string, costId: string): Promise<void> =>
    apiRequest(`/evictions/${evictionId}/costs/${costId}`, { method: 'DELETE' }),
};

// Documents API (files stored in R2)
export interface AppDocument {
  id: string;
  name: string;
  contentType?: string;
  size: number;
  propertyId?: string;
  tenantId?: string;
  createdAt: number;
  /** Office user who uploaded it, or null when the applicant uploaded a signed
   * copy themselves through the public signing link. */
  uploadedBy?: string | null;
}

export const documentsApi = {
  list: (tenantId?: string): Promise<AppDocument[]> =>
    apiRequest(`/documents${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''}`),
  upload: async (file: File, opts: { tenantId?: string; propertyId?: string; officeOnly?: boolean } = {}): Promise<AppDocument> => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts.tenantId) fd.append('tenantId', opts.tenantId);
    if (opts.propertyId) fd.append('propertyId', opts.propertyId);
    if (opts.officeOnly) fd.append('officeOnly', '1');
    // Note: no Content-Type header — the browser sets the multipart boundary.
    const res = await fetch(`${API_BASE}/documents`, { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.data !== undefined ? data.data : data;
  },
  delete: (id: string) => apiRequest(`/documents/${id}`, { method: 'DELETE' }),
  downloadUrl: (id: string) => `${API_BASE}/documents/${id}`,
};

// Document Templates (central template library)
export interface DocumentTemplate {
  id: string;
  name: string;
  category: 'lease' | 'application' | 'rules' | 'addendum' | 'other';
  description?: string;
  driveFileId: string;
  contentType?: string;
  size: number;
  uploadedBy?: string;
  createdAt: number;
  updatedAt: number;
}

export const templateApi = {
  list: (): Promise<DocumentTemplate[]> => apiRequest('/document-templates'),
  upload: async (file: File, name: string, category: string, description?: string): Promise<DocumentTemplate> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name);
    fd.append('category', category);
    if (description) fd.append('description', description);
    const res = await fetch(`${API_BASE}/document-templates`, { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.data !== undefined ? data.data : data;
  },
  update: (id: string, data: { name?: string; category?: string; description?: string }): Promise<DocumentTemplate> =>
    apiRequest(`/document-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) => apiRequest(`/document-templates/${id}`, { method: 'DELETE' }),
  /** Send a single template to a prospective tenant. */
  send: (templateId: string, prospectiveTenantId: string): Promise<{ documentId: string; emailed: boolean; statusAdvanced: boolean }> =>
    apiRequest(`/document-templates/${templateId}/send`, {
      method: 'POST',
      body: JSON.stringify({ prospectiveTenantId }),
    }),
  /** Send multiple templates to a prospective tenant at once. */
  sendBatch: (templateIds: string[], prospectiveTenantId: string): Promise<{
    sent: { documentId: string; templateName: string }[];
    emailed: boolean;
    statusAdvanced: boolean;
  }> =>
    apiRequest('/document-templates/send-batch', {
      method: 'POST',
      body: JSON.stringify({ templateIds, prospectiveTenantId }),
    }),
};

// Google Drive API (document storage connection)
export interface GoogleStatus {
  connected: boolean;
}

export const googleApi = {
  // Note: apiRequest already prefixes with API_BASE ('/api'), so these paths
  // must not repeat it (unlike the raw fetch to /api/google/connect below,
  // which is a full page redirect and never goes through apiRequest).
  status: (): Promise<GoogleStatus> => apiRequest('/google/status'),
  disconnect: () => apiRequest('/google/disconnect', { method: 'POST' }),
};

// Master rent spreadsheet (a Google Sheet in Drive that mirrors tenants/payments)
export const rentSheetApi = {
  // connected = Drive linked; url = the sheet's link, null until it exists yet
  get: (): Promise<{ connected: boolean; url: string | null }> => apiRequest('/rent-sheet'),
  // Forces creation (if needed) + a full rebuild; returns the sheet's link
  sync: (): Promise<{ url: string }> => apiRequest('/rent-sheet/sync', { method: 'POST' }),
};

// Portal API (tenant and realtor self-service)
// Note: apiRequest already prefixes with API_BASE ('/api'), so these paths
// must not repeat it: pass '/portal/...', not '/api/portal/...'.

/**
 * A lease as the portal serializer returns it (serializePortalLease): an
 * allowlist that omits `notes` and `tenantIds`, unlike the full `Lease` shape
 * the management app uses. It DOES carry `pauses`, so the tenant's pages can
 * gate months with `leasesOwingMonth` and agree with the owner's Rent
 * Management about which months were owed. `tenantIds` is filled with an empty
 * default by callers that build a full `Lease`.
 */
export interface PortalLease {
  id: string;
  unitId?: string;
  propertyId?: string;
  leaseType: LeaseType;
  startDate?: string;
  endDate?: string;
  monthlyRent: number;
  securityDeposit?: number;
  status: LeaseStatus;
  pauses: { pausedAt: string; resumedAt?: string }[];
  renewalStatus?: 'draft' | 'pending' | 'approved' | 'rejected' | 'cancelled';
  renewedFromLeaseId?: string;
  rentDueDay?: number;
}

export interface PortalMeResponse {
  tenant: Tenant;
  lease: PortalLease | null;
  siblingLeases?: PortalLease[];
  unit: Unit | null;
  property: Property | null;
  // The owner's "how to pay" text from Settings; the portal adds the memo.
  paymentInstructions?: string;
  // The past-due threshold (months) from Settings; defaults to 2.
  pastDueMonths?: number;
  // The day of the month rent is due, from Settings; defaults to 1.
  rentDueDay?: number;
}

export interface PortalMoveInFee {
  amount: number;
  paidDate?: string;
  method?: string;
  receiptDocumentId?: string;
}

export interface PortalPaymentsResponse {
  lease: PortalLease | null;
  payments: PortalPayment[];
  allocations?: PaymentAllocation[];
  moveInFee?: PortalMoveInFee | null;
  creditBalance?: number;
}

/**
 * A tenant as the portal's allowlist serializer returns it
 * (serializePortalTenant): same shape as `Tenant` minus `notes`, which is the
 * owner's private note and never travels to a tenant or realtor session.
 */
/** Where a tenant lives: their unit number and the property's exact address,
 * resolved server side from their most recent lease. */
export interface TenantUnit {
  unitNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface PortalPerson {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  emergencyContact?: { name: string; phone: string; relationship: string };
  unit?: TenantUnit;
  photoUrl?: string | null;
  // Approval state of this tenant's placement, for the realtor's view:
  // 'under_review' (draft with the office), 'approved' (active), or 'none'.
  approval?: 'under_review' | 'approved' | 'none';
}

/** A tenant as a realtor's list sees them: the allowlisted person plus their
 * unit and address (via PortalPerson.unit). */
export type RealtorTenantSummary = PortalPerson;

export interface HouseholdMember {
  id: string;
  leaseId: string;
  name: string;
  phone: string | null;
  relationship: string | null;
  createdAt: number;
}
export interface HouseholdInput {
  name: string;
  phone?: string;
  relationship?: string;
}

export interface RealtorContact {
  name: string | null;
  email: string;
  phone: string | null;
  companyName?: string | null;
}
export interface RealtorMe {
  profile: RealtorContact & { photoUrl: string | null };
  tenantsPlaced: number;
  tenantsInWindow: number;
}

/** A vacant unit as a realtor sees it for marketing: no tenant/lease data,
 * just the unit's own specs, the asking rent, and the property's address. */
export interface AvailableUnit {
  id: string;
  unitNumber: string;
  bedrooms?: number;
  bathrooms?: number;
  squareFeet?: number;
  monthlyRent: number;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

/** A maintenance request as a portal user sees it: the base request plus the
 * few contact fields the endpoint attaches (handyman for tenants; tenant and
 * location for handymen). */
export interface PortalMaintenanceRequest extends MaintenanceRequest {
  handymanName?: string;
  handymanPhone?: string;
  tenantName?: string;
  tenantPhone?: string;
  locationLabel?: string;
}

/** A handyman's portal view: open jobs matching their trades, plus their own. */
export interface HandymanJobsResponse {
  available: PortalMaintenanceRequest[];
  mine: PortalMaintenanceRequest[];
}

/** One message in a tenant/office thread. createdAt is unix seconds. */
export interface Message {
  id: string;
  tenantId: string;
  senderRole: 'tenant' | 'office';
  senderName?: string;
  body: string;
  createdAt: number;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentType?: string;
}

/** One message in an office<->handyman (vendor) thread. */
export interface VendorMessage {
  id: string;
  handymanId: string;
  senderRole: 'office' | 'handyman';
  senderName?: string;
  body: string;
  createdAt: number;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentType?: string;
}

/** A row in the office's vendor inbox: one handyman's thread summary. */
export interface VendorThread {
  handymanId: string;
  name: string;
  phone: string | null;
  lastBody: string | null;
  lastSender: 'office' | 'handyman' | null;
  lastAt: number | null;
  unread: number;
}

/** One message in an office<->realtor thread. */
export interface RealtorMessage {
  id: string;
  realtorUserId: string;
  senderRole: 'office' | 'realtor';
  senderName?: string;
  body: string;
  createdAt: number;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentType?: string;
}

/** A row in the office's realtor inbox. */
export interface RealtorThread {
  realtorUserId: string;
  name: string;
  phone: string | null;
  companyName: string | null;
  lastBody: string | null;
  lastSender: 'office' | 'realtor' | null;
  lastAt: number | null;
  unread: number;
}

/** A row in the office inbox: one tenant's thread summary. */
export interface MessageThread {
  tenantId: string;
  firstName: string;
  lastName: string;
  propertyName: string | null;
  propertyAddress: string | null;
  unitNumber: string | null;
  lastBody: string | null;
  lastSender: 'tenant' | 'office' | null;
  lastAt: number;
  unread: number;
}

/** "Maple Court, Unit 2" style label from a property/unit, or '' when unknown. */
export function placeLabel(
  p: { propertyName?: string | null; propertyAddress?: string | null; unitNumber?: string | null }
): string {
  const prop = p.propertyName || p.propertyAddress || '';
  const unit = p.unitNumber ? `Unit ${p.unitNumber}` : '';
  return [prop, unit].filter(Boolean).join(', ');
}

export interface StripePaymentMethod {
  id: string;
  type: 'us_bank_account' | 'card';
  last4: string;
  bankName?: string;
  accountType?: string;
  brand?: string;
  expMonth?: number;
  expYear?: number;
}

export interface PortalAnnouncement {
  id: string;
  title: string;
  body: string;
  created_at: number;
  expires_at: string | null;
  property_name: string | null;
}

export const portalApi = {
  me: (): Promise<PortalMeResponse> => apiRequest('/portal/me'),
  // The tenant's linked realtor(s), contact info only, always visible.
  myRealtors: (): Promise<RealtorContact[]> => apiRequest('/portal/my-realtors'),
  // The realtor's own profile plus summary counts, for their dashboard.
  realtorMe: (): Promise<RealtorMe> => apiRequest('/portal/realtor/me'),
  // { lease, payments }, not a bare array: see PortalPaymentsResponse. No
  // payer field travels on any payment row (Belle's decision: on a shared
  // lease a tenant never sees who paid what).
  payments: (): Promise<PortalPaymentsResponse> => apiRequest('/portal/payments'),
  // A tenant generates the receipt for one of their own paid payments; returns
  // its document id so the page can link straight to the download.
  generateReceipt: (paymentId: string): Promise<{ receiptDocumentId: string }> =>
    apiRequest(`/portal/payments/${paymentId}/receipt`, { method: 'POST' }),
  // A tenant adds their emergency contact (only allowed when none is on file).
  setEmergencyContact: (data: { name: string; phone: string; relationship: string }): Promise<{ name: string; phone: string; relationship: string }> =>
    apiRequest('/portal/emergency-contact', { method: 'POST', body: JSON.stringify(data) }),
  documents: (tenantId?: string): Promise<AppDocument[]> =>
    apiRequest(`/portal/documents${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''}`),
  // Multipart, like documentsApi.upload: no Content-Type header, so the
  // browser sets the multipart boundary itself.
  uploadDocument: async (file: File, tenantId: string): Promise<AppDocument> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tenantId', tenantId);
    const res = await fetch(`${API_BASE}/portal/documents`, { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.data !== undefined ? data.data : data;
  },
  // NOT documentsApi.downloadUrl: that builds /api/documents/:id, the staff
  // route (requirePermission('tenants_view')), which a tenant or realtor
  // session has no permission to hit. This is the portal's own route.
  downloadUrl: (id: string) => `${API_BASE}/portal/documents/${id}`,
  realtorTenants: (): Promise<RealtorTenantSummary[]> => apiRequest('/portal/realtor/tenants'),
  realtorTenant: (id: string): Promise<PortalPerson> => apiRequest(`/portal/realtor/tenants/${id}`),
  addRealtorTenant: (data: {
    firstName: string; lastName: string; email?: string; phone?: string;
    emergencyName?: string; emergencyPhone?: string; emergencyRelationship?: string;
    unitId?: string; monthlyRent?: number; startDate?: string; endDate?: string;
  }): Promise<PortalPerson> =>
    apiRequest('/portal/realtor/tenants', { method: 'POST', body: JSON.stringify(data) }),
  updateRealtorTenant: (id: string, data: {
    firstName: string; lastName: string; email: string; phone: string;
    emergencyName?: string; emergencyPhone?: string; emergencyRelationship?: string;
  }): Promise<PortalPerson> =>
    apiRequest(`/portal/realtor/tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  realtorHousehold: {
    list: (tenantId: string): Promise<HouseholdMember[]> =>
      apiRequest(`/portal/realtor/household?tenantId=${encodeURIComponent(tenantId)}`),
    add: (tenantId: string, data: HouseholdInput): Promise<HouseholdMember> =>
      apiRequest('/portal/realtor/household', { method: 'POST', body: JSON.stringify({ tenantId, ...data }) }),
    update: (id: string, data: HouseholdInput): Promise<HouseholdMember> =>
      apiRequest(`/portal/realtor/household/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    remove: (id: string): Promise<{ success: boolean }> =>
      apiRequest(`/portal/realtor/household/${id}`, { method: 'DELETE' }),
  },
  // Vacant units the realtor may market, asking rent included on purpose.
  availableUnits: (): Promise<AvailableUnit[]> => apiRequest('/portal/realtor/available-units'),
  // A tenant's own maintenance requests, with the assigned handyman's name and
  // phone attached when there is one.
  maintenance: (): Promise<PortalMaintenanceRequest[]> => apiRequest('/portal/maintenance'),
  createMaintenance: (data: {
    title: string; category: string; description?: string;
    availability: { date: string; start: string; end: string }[];
  }): Promise<MaintenanceRequest> =>
    apiRequest('/portal/maintenance', { method: 'POST', body: JSON.stringify(data) }),
  // Attach (or replace) a photo on one of the tenant's own requests. Multipart,
  // like the other photo uploads: postPhoto sets the boundary itself.
  uploadMaintenancePhoto: (id: string, file: Blob): Promise<{ photoUrl: string }> =>
    postPhoto(`/portal/maintenance/${id}/photo`, file),
  // A handyman's own profile, and updating their own contact details.
  handymanMe: (): Promise<Handyman> => apiRequest('/portal/handyman/me'),
  updateHandymanMe: (data: {
    name: string; phone?: string; address?: string; city?: string; state?: string; zipCode?: string;
  }): Promise<Handyman> =>
    apiRequest('/portal/handyman/me', { method: 'PUT', body: JSON.stringify(data) }),
  // A handyman's own jobs and the open jobs matching their trades.
  handymanJobs: (): Promise<HandymanJobsResponse> => apiRequest('/portal/handyman/jobs'),
  claimJob: (id: string): Promise<PortalMaintenanceRequest> =>
    apiRequest(`/portal/handyman/jobs/${id}/claim`, { method: 'POST' }),
  scheduleJob: (id: string, scheduledFor: string): Promise<PortalMaintenanceRequest> =>
    apiRequest(`/portal/handyman/jobs/${id}/schedule`, { method: 'POST', body: JSON.stringify({ scheduledFor }) }),
  jobStatus: (id: string, status: 'in_progress' | 'completed'): Promise<PortalMaintenanceRequest> =>
    apiRequest(`/portal/handyman/jobs/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  // The property + unit list a handyman needs to self-report where they worked.
  handymanPlaces: (): Promise<{ properties: { id: string; name: string; address: string }[]; units: { id: string; propertyId: string; unitNumber: string }[] }> =>
    apiRequest('/portal/handyman/places'),
  // A handyman logs work they did; created as completed + pending admin approval.
  reportWork: (data: {
    items: Array<{ propertyId: string; unitId?: string; title: string; cost: number }>;
    description?: string; category: string; date: string;
  }): Promise<PortalMaintenanceRequest | PortalMaintenanceRequest[]> =>
    apiRequest('/portal/handyman/report', { method: 'POST', body: JSON.stringify(data) }),
  // Handyman uploads an invoice for a job approved for invoicing. Multipart form.
  submitInvoice: async (jobId: string, data: {
    files: File[];
    invoiceNumber: string;
    invoiceDate: string;
    laborAmount: number;
    materialAmount: number;
    totalAmount: number;
    notes?: string;
  }): Promise<PortalMaintenanceRequest> => {
    const fd = new FormData();
    for (const f of data.files) fd.append('files[]', f);
    fd.append('invoiceNumber', data.invoiceNumber);
    fd.append('invoiceDate', data.invoiceDate);
    fd.append('laborAmount', String(data.laborAmount));
    fd.append('materialAmount', String(data.materialAmount));
    fd.append('totalAmount', String(data.totalAmount));
    if (data.notes) fd.append('notes', data.notes);
    const res = await fetch(`${API_BASE}/portal/handyman/jobs/${jobId}/invoice`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const json = await res.json();
    return json.data !== undefined ? json.data : json;
  },
  // Web push: register/unregister this device, and send a test to yourself.
  pushSubscribe: (sub: { endpoint?: string | null; keys?: { p256dh?: string; auth?: string } }): Promise<{ success: boolean }> =>
    apiRequest('/portal/push/subscribe', { method: 'POST', body: JSON.stringify(sub) }),
  pushUnsubscribe: (endpoint: string): Promise<{ success: boolean }> =>
    apiRequest('/portal/push/subscribe', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),
  pushTest: (): Promise<{ success: boolean }> => apiRequest('/portal/push/test', { method: 'POST' }),
  household: {
    list: (): Promise<HouseholdMember[]> => apiRequest('/portal/household'),
    add: (data: HouseholdInput): Promise<HouseholdMember> =>
      apiRequest('/portal/household', { method: 'POST', body: JSON.stringify(data) }),
  },
  // The tenant's own thread with the office. Fetching it marks office replies
  // read; count is the cheap unread lookup for the nav badge.
  messages: (): Promise<{ messages: Message[] }> => apiRequest('/portal/messages'),
  messagesUnread: (): Promise<{ count: number }> => apiRequest('/portal/messages?count=1'),
  sendMessage: (body: string, file?: File | null): Promise<Message> =>
    postMessageForm('/portal/messages', body, file),
  // A handyman's own thread with the office (vendor messaging).
  vendorMessages: (): Promise<{ messages: VendorMessage[] }> => apiRequest('/portal/handyman/messages'),
  vendorMessagesUnread: (): Promise<{ count: number }> => apiRequest('/portal/handyman/messages?count=1'),
  sendVendorMessage: (body: string, file?: File | null): Promise<VendorMessage> =>
    postMessageForm('/portal/handyman/messages', body, file),
  // A realtor's own thread with the office.
  realtorMessages: (): Promise<{ messages: RealtorMessage[] }> => apiRequest('/portal/realtor/messages'),
  realtorMessagesUnread: (): Promise<{ count: number }> => apiRequest('/portal/realtor/messages?count=1'),
  sendRealtorMessage: (body: string, file?: File | null): Promise<RealtorMessage> =>
    postMessageForm('/portal/realtor/messages', body, file),
  announcements: (): Promise<PortalAnnouncement[]> => apiRequest('/portal/announcements'),
  stripe: {
    createSetupIntent: (type?: 'us_bank_account' | 'card'): Promise<{ clientSecret: string; type: string }> =>
      apiRequest('/portal/stripe/setup-intent', { method: 'POST', body: JSON.stringify({ type: type || 'us_bank_account' }) }),
    paymentMethods: (): Promise<StripePaymentMethod[]> =>
      apiRequest('/portal/stripe/payment-methods'),
    removePaymentMethod: (paymentMethodId: string): Promise<{ success: boolean }> =>
      apiRequest('/portal/stripe/payment-methods', { method: 'DELETE', body: JSON.stringify({ paymentMethodId }) }),
    payRent: (data: { paymentMethodId: string; amount: number; month: number; year: number; leaseId: string; methodType?: 'us_bank_account' | 'card' }): Promise<{ paymentIntentId: string; clientSecret: string; status: string; fee: number; totalAmount: number }> =>
      apiRequest('/portal/stripe/pay-rent', { method: 'POST', body: JSON.stringify(data) }),
    getAutopay: (): Promise<{ enabled: boolean; paymentMethodId: string | null; methodType: string }> =>
      apiRequest('/portal/stripe/autopay'),
    setAutopay: (data: { enabled: boolean; paymentMethodId?: string }): Promise<{ success: boolean; enabled: boolean }> =>
      apiRequest('/portal/stripe/autopay', { method: 'POST', body: JSON.stringify(data) }),
  },
};

export const photoApi = {
  uploadSelf: (file: Blob): Promise<{ photoUrl: string }> => postPhoto('/portal/photo', file),
  removeSelf: (): Promise<{ success: boolean }> => apiRequest('/portal/photo', { method: 'DELETE' }),
  uploadTenant: (tenantId: string, file: Blob): Promise<{ photoUrl: string }> => postPhoto(`/tenants/${tenantId}/photo`, file),
  removeTenant: (tenantId: string): Promise<{ success: boolean }> => apiRequest(`/tenants/${tenantId}/photo`, { method: 'DELETE' }),
  uploadUser: (userId: string, file: Blob): Promise<{ photoUrl: string }> => postPhoto(`/admin/users/${userId}/photo`, file),
  removeUser: (userId: string): Promise<{ success: boolean }> => apiRequest(`/admin/users/${userId}/photo`, { method: 'DELETE' }),
};

async function postPhoto(path: string, file: Blob): Promise<{ photoUrl: string }> {
  const fd = new FormData();
  fd.append('photo', file, 'photo.jpg');
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.data !== undefined ? data.data : data;
}

/** Send a message (text plus an optional attachment) as multipart form-data. */
async function postMessageForm<T>(path: string, body: string, file?: File | null): Promise<T> {
  const fd = new FormData();
  fd.append('body', body);
  if (file) fd.append('file', file);
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to send' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return (data.data !== undefined ? data.data : data) as T;
}

// Maintenance API
export const maintenanceApi = {
  getAll: (): Promise<MaintenanceRequest[]> => apiRequest('/maintenance'),
  getById: (id: string): Promise<MaintenanceRequest> => apiRequest(`/maintenance/${id}`),
  create: (data: Omit<MaintenanceRequest, 'id'>): Promise<MaintenanceRequest> =>
    apiRequest('/maintenance', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<MaintenanceRequest> & Pick<MaintenanceRequest, 'title' | 'status'>): Promise<MaintenanceRequest> =>
    apiRequest(`/maintenance/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiRequest(`/maintenance/${id}`, { method: 'DELETE' }),
  // Release a submitted job so any trade-matched handyman can see and claim it.
  release: (id: string): Promise<MaintenanceRequest> =>
    apiRequest(`/maintenance/${id}/release`, { method: 'POST' }),
  // Assign (or clear, with handymanId null) a handyman. Notifies them + tenant.
  assign: (id: string, handymanId: string | null): Promise<MaintenanceRequest> =>
    apiRequest(`/maintenance/${id}/assign`, { method: 'POST', body: JSON.stringify({ handymanId }) }),
  // Record paying the handyman: sets the cost, stamps paid, counts in Finances.
  pay: (id: string, cost: number): Promise<MaintenanceRequest> =>
    apiRequest(`/maintenance/${id}/pay`, { method: 'POST', body: JSON.stringify({ cost }) }),
  // Approve a handyman-reported job (and its cost). With an assigned handyman
  // this transitions to approved_for_invoicing (no expense yet); without one
  // it writes the expense immediately. Pass skipInvoice:true to force the
  // direct expense path even when a handyman is assigned.
  approve: (id: string, cost: number, skipInvoice?: boolean): Promise<MaintenanceRequest> =>
    apiRequest(`/maintenance/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ cost, ...(skipInvoice ? { skipInvoice: true } : {}) }),
    }),
  // Admin reviews a submitted invoice: approve, reject, or request revision.
  invoiceReview: (id: string, action: 'approve' | 'reject' | 'revision', reason?: string): Promise<MaintenanceRequest> =>
    apiRequest(`/maintenance/${id}/invoice-review`, {
      method: 'POST',
      body: JSON.stringify({ action, reason }),
    }),
  // Fetch the status change history log for a request.
  getHistory: (id: string): Promise<import('../types').MaintenanceStatusLog[]> =>
    apiRequest(`/maintenance/${id}/history`),
  // Generate a secure, no-login vendor invoice form link and optionally email it.
  vendorLink: (id: string, opts: { vendorEmail?: string; vendorName?: string }): Promise<{
    id: string; token: string; url: string; emailed: boolean; emailedTo: string | null;
  }> =>
    apiRequest(`/maintenance/${id}/vendor-link`, { method: 'POST', body: JSON.stringify(opts) }),
};

// Handymen roster API (admin side).
export interface HandymanInput {
  name: string;
  companyName?: string;
  phone?: string;
  email?: string;
  trades: string[];
  isActive?: boolean;
}

/** The result of adding or inviting a handyman: the row plus whether mail went out. */
export interface HandymanInviteResult {
  handyman?: Handyman;
  emailSent: boolean;
  inviteUrl?: string;
}

/** One entry in the activity log: who did what, to which record, and when. */
export interface ActivityEntry {
  id: string;
  userId?: string;
  userName?: string;
  userRole?: string;
  module?: string;
  action: string;
  description?: string;
  targetType?: string;
  targetId?: string;
  targetName?: string;
  propertyId?: string;
  unitId?: string;
  tenantId?: string;
  previousValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  method: string;
  statusCode?: number;
  createdAt: number;
}

export interface ActivityFilter {
  limit?: number;
  offset?: number;
  module?: string;
  userId?: string;
  propertyId?: string;
  tenantId?: string;
  targetType?: string;
  targetId?: string;
  search?: string;
  dateFrom?: number;
  dateTo?: number;
}

export interface ActivityResponse {
  data: ActivityEntry[];
  total: number;
}

export const activityApi = {
  list: async (filter: ActivityFilter = {}): Promise<ActivityResponse> => {
    const params = new URLSearchParams();
    if (filter.limit) params.set('limit', String(filter.limit));
    if (filter.offset) params.set('offset', String(filter.offset));
    if (filter.module) params.set('module', filter.module);
    if (filter.userId) params.set('userId', filter.userId);
    if (filter.propertyId) params.set('propertyId', filter.propertyId);
    if (filter.tenantId) params.set('tenantId', filter.tenantId);
    if (filter.targetType) params.set('targetType', filter.targetType);
    if (filter.targetId) params.set('targetId', filter.targetId);
    if (filter.search) params.set('search', filter.search);
    if (filter.dateFrom) params.set('dateFrom', String(filter.dateFrom));
    if (filter.dateTo) params.set('dateTo', String(filter.dateTo));
    // apiRequest unwraps { data } automatically, which drops `total`. Fetch the
    // raw response so both data AND total reach the caller.
    const url = `${API_BASE}/activity?${params.toString()}`;
    const res = await fetch(url, { credentials: 'include', headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return { data: json.data ?? [], total: json.total ?? 0 };
  },
};

export const handymenApi = {
  getAll: (): Promise<Handyman[]> => apiRequest('/handymen'),
  create: (data: HandymanInput): Promise<HandymanInviteResult> =>
    apiRequest('/handymen', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: HandymanInput): Promise<Handyman> =>
    apiRequest(`/handymen/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  // Hard delete: removes the roster row and their login. Deactivate (a pause)
  // goes through update({ isActive: false }) instead.
  remove: (id: string) =>
    apiRequest(`/handymen/${id}`, { method: 'DELETE' }),
  invite: (id: string): Promise<HandymanInviteResult> =>
    apiRequest(`/handymen/${id}/invite`, { method: 'POST' }),
};

export type ProspectiveStatus = 'applied' | 'docs_sent' | 'signed' | 'converted' | 'rejected';

/** An applicant, before they become an active tenant. No portal login. */
export interface ProspectiveTenant {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  notes?: string;
  status: ProspectiveStatus;
  convertedTenantId?: string;
  createdAt: number;
}

export interface ProspectiveInput {
  firstName: string; lastName: string; email?: string; phone?: string; notes?: string;
}

// Prospective tenants (applicants): records, their files, and conversion.
export const prospectiveApi = {
  list: (): Promise<ProspectiveTenant[]> => apiRequest('/prospective-tenants'),
  get: (id: string): Promise<ProspectiveTenant> => apiRequest(`/prospective-tenants/${id}`),
  create: (data: ProspectiveInput): Promise<ProspectiveTenant> =>
    apiRequest('/prospective-tenants', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: ProspectiveInput & { status?: ProspectiveStatus }): Promise<ProspectiveTenant> =>
    apiRequest(`/prospective-tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) => apiRequest(`/prospective-tenants/${id}`, { method: 'DELETE' }),
  documents: (id: string): Promise<AppDocument[]> => apiRequest(`/prospective-tenants/${id}/documents`),
  uploadDocument: async (id: string, file: File): Promise<AppDocument> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/prospective-tenants/${id}/documents`, { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.data !== undefined ? data.data : data;
  },
  convert: (id: string, data: { unitId: string; monthlyRent: number; moveInFee?: number; moveInFeePaid?: boolean; startDate?: string; endDate?: string }): Promise<{ tenantId: string }> =>
    apiRequest(`/prospective-tenants/${id}/convert`, { method: 'POST', body: JSON.stringify(data) }),
  // Mint (or reuse) the applicant's secure, no-login signing token; emails it to
  // the applicant automatically when they have an email on file.
  signLink: (id: string): Promise<{ token: string; emailedTo?: string | null }> =>
    apiRequest(`/prospective-tenants/${id}/sign-link`, { method: 'POST' }),
};

// PUBLIC signing flow (no login), used by the /sign/:token page.
export interface SigningInfo {
  firstName: string;
  status: ProspectiveStatus;
  /** Documents the office sent for the applicant to sign. */
  documents: { id: string; name: string }[];
  /** Signed copies the applicant has already uploaded. */
  uploaded: { id: string; name: string }[];
}
export const signingApi = {
  info: (token: string): Promise<SigningInfo> => apiRequest(`/sign/${token}`),
  documentUrl: (token: string, docId: string) => `${API_BASE}/sign/${token}/document/${docId}`,
  upload: async (token: string, file: File): Promise<{ id: string; name: string }> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/sign/${token}/upload`, { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.data !== undefined ? data.data : data;
  },
  complete: (token: string): Promise<{ success: boolean }> =>
    apiRequest(`/sign/${token}/complete`, { method: 'POST' }),
};

// PUBLIC vendor invoice form (no login), used by the /vendor-form/:token page.
export interface VendorFormInfo {
  status: string;
  alreadySubmitted: boolean;
  vendorName: string;
  companyName: string;
  vendorEmail: string;
  vendorPhone: string;
  propertyId: string;
  unitId: string;
  workDescription: string;
  amount: number | null;
  paymentMethod: string;
  jobTitle: string;
  propName: string;
  propAddress: string;
  rejectionReason: string | null;
  properties: { id: string; name: string; address: string }[];
  units: { id: string; propertyId: string; unitNumber: string }[];
}
export const vendorFormApi = {
  info: (token: string): Promise<VendorFormInfo> => apiRequest(`/vendor-form/${token}`),
  submit: (token: string, data: Record<string, unknown>): Promise<{ success: boolean }> =>
    apiRequest(`/vendor-form/${token}`, { method: 'POST', body: JSON.stringify(data) }),
};

// Admin vendor submissions management.
export interface VendorSubmission {
  id: string;
  token: string;
  maintenanceRequestId: string | null;
  vendorName: string;
  companyName: string;
  vendorEmail: string;
  vendorPhone: string;
  propertyId: string | null;
  unitId: string | null;
  workDescription: string;
  amount: number | null;
  paymentMethod: string;
  status: 'draft' | 'submitted' | 'approved' | 'paid' | 'rejected';
  approvedBy: string | null;
  approvedAt: string | null;
  paidAt: string | null;
  rejectionReason: string | null;
  adminNotes: string | null;
  expenseId: string | null;
  createdAt: number;
  submittedAt: number | null;
  updatedAt: number;
  jobTitle: string | null;
  propName: string | null;
  propAddress: string | null;
  unitNumber: string | null;
}
export const vendorSubmissionsApi = {
  list: (): Promise<VendorSubmission[]> => apiRequest('/vendor-submissions'),
  updateStatus: (id: string, action: 'approve' | 'paid' | 'reject', extra?: {
    notes?: string; rejectionReason?: string;
  }): Promise<VendorSubmission> =>
    apiRequest(`/vendor-submissions/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ action, ...extra }),
    }),
  remove: (id: string): Promise<void> =>
    apiRequest(`/vendor-submissions/${id}`, { method: 'DELETE' }),
};

// Office side of tenant messaging: the inbox, one tenant's thread, and replies.
export const messagesApi = {
  threads: (): Promise<{ threads: MessageThread[] }> => apiRequest('/messages'),
  unreadCount: (): Promise<{ count: number }> => apiRequest('/messages?count=1'),
  thread: (tenantId: string): Promise<{
    tenantId: string; tenantName: string;
    propertyName: string | null; propertyAddress: string | null; unitNumber: string | null;
    messages: Message[];
  }> =>
    apiRequest(`/messages/${tenantId}`),
  reply: (tenantId: string, body: string, file?: File | null): Promise<Message> =>
    postMessageForm(`/messages/${tenantId}`, body, file),
};

// Office side of vendor (handyman) messaging: the inbox, one thread, and replies.
export const vendorMessagesApi = {
  threads: (): Promise<{ threads: VendorThread[] }> => apiRequest('/handyman-messages'),
  unreadCount: (): Promise<{ count: number }> => apiRequest('/handyman-messages?count=1'),
  thread: (handymanId: string): Promise<{ handymanId: string; handymanName: string; messages: VendorMessage[] }> =>
    apiRequest(`/handyman-messages/${handymanId}`),
  reply: (handymanId: string, body: string, file?: File | null): Promise<VendorMessage> =>
    postMessageForm(`/handyman-messages/${handymanId}`, body, file),
};

export const realtorMessagesApi = {
  threads: (): Promise<{ threads: RealtorThread[] }> => apiRequest('/realtor-messages'),
  unreadCount: (): Promise<{ count: number }> => apiRequest('/realtor-messages?count=1'),
  thread: (realtorId: string): Promise<{ realtorId: string; realtorName: string; messages: RealtorMessage[] }> =>
    apiRequest(`/realtor-messages/${realtorId}`),
  reply: (realtorId: string, body: string, file?: File | null): Promise<RealtorMessage> =>
    postMessageForm(`/realtor-messages/${realtorId}`, body, file),
};

export const expenseImportApi = {
  list: (): Promise<ExpenseImport[]> => apiRequest('/expense-imports'),
  upload: async (file: File, columnMapping?: Record<string, string>): Promise<{ id: string; fileName: string; totalRows: number; validRows: number; errorRows: number; duplicateRows: number; columnMapping: Record<string, string> }> => {
    const form = new FormData();
    form.append('file', file);
    if (columnMapping) form.append('columnMapping', JSON.stringify(columnMapping));
    const res = await fetch(`${API_BASE}/expense-imports`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const json = await res.json();
    return json.data ?? json;
  },
  get: (id: string): Promise<ExpenseImportDetail> => apiRequest(`/expense-imports/${id}`),
  delete: (id: string): Promise<void> => apiRequest(`/expense-imports/${id}`, { method: 'DELETE' }),
  updateRows: (importId: string, rows: Array<{ id: string; [k: string]: unknown }>): Promise<{ updatedCount: number }> =>
    apiRequest(`/expense-imports/${importId}/rows`, { method: 'PUT', body: JSON.stringify({ rows }) }),
  merge: (id: string): Promise<{ mergedRows: number }> =>
    apiRequest(`/expense-imports/${id}/merge`, { method: 'POST' }),
  rollback: (id: string): Promise<{ removedExpenses: number }> =>
    apiRequest(`/expense-imports/${id}/rollback`, { method: 'POST' }),
};

export const calendarApi = {
  list: (): Promise<CalendarEvent[]> => apiRequest('/calendar'),
  get: (id: string): Promise<CalendarEvent> => apiRequest(`/calendar/${id}`),
  create: (event: Partial<CalendarEvent>): Promise<CalendarEvent> =>
    apiRequest('/calendar', { method: 'POST', body: JSON.stringify(event) }),
  update: (id: string, event: Partial<CalendarEvent>): Promise<CalendarEvent> =>
    apiRequest(`/calendar/${id}`, { method: 'PUT', body: JSON.stringify(event) }),
  delete: (id: string): Promise<void> => apiRequest(`/calendar/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Capital Expense Projects
// ---------------------------------------------------------------------------

export const capitalProjectsApi = {
  list: (): Promise<CapitalProject[]> => apiRequest('/capital-projects'),
  get: (id: string): Promise<CapitalProjectDetail> => apiRequest(`/capital-projects/${id}`),
  create: (data: Partial<CapitalProject>): Promise<CapitalProject> =>
    apiRequest('/capital-projects', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<CapitalProject>): Promise<CapitalProject> =>
    apiRequest(`/capital-projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string): Promise<void> =>
    apiRequest(`/capital-projects/${id}`, { method: 'DELETE' }),
  linkExpenses: (id: string, expenseIds: string[]): Promise<{ linked: number; totalCost: number; expenseCount: number }> =>
    apiRequest(`/capital-projects/${id}/expenses`, { method: 'POST', body: JSON.stringify({ expenseIds }) }),
  unlinkExpenses: (id: string, expenseIds: string[]): Promise<{ unlinked: number }> =>
    apiRequest(`/capital-projects/${id}/expenses`, { method: 'DELETE', body: JSON.stringify({ expenseIds }) }),
  uploadReceipt: async (id: string, file: File): Promise<{ driveFileId: string; name: string }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API_BASE}/capital-projects/${id}/receipt`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    const json = await res.json() as { success?: boolean; error?: string; data?: { driveFileId: string; name: string } };
    if (!res.ok || !json.success) throw new Error(json.error || 'Upload failed');
    return json.data!;
  },
};

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------

export interface Announcement {
  id: string;
  title: string;
  body: string;
  property_id: string | null;
  property_name: string | null;
  created_by: string;
  author_name: string | null;
  author_email: string | null;
  created_at: number;
  expires_at: string | null;
}

export interface AnnouncementInput {
  title: string;
  body: string;
  propertyIds?: string[];
  expiresAt?: string;
}

export const announcementsApi = {
  list: (): Promise<Announcement[]> => apiRequest('/announcements'),
  create: (data: AnnouncementInput): Promise<{ ids: string[]; recipientCount: number }> =>
    apiRequest('/announcements', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: { title?: string; body?: string; expiresAt?: string | null }): Promise<void> =>
    apiRequest(`/announcements/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string): Promise<void> => apiRequest(`/announcements/${id}`, { method: 'DELETE' }),
};

// AI Assistant
// ---------------------------------------------------------------------------

export interface AIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolsUsed?: string[];
  createdAt?: number;
}

export interface AIConversation {
  id: string;
  title: string;
  messageCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface AIConversationDetail {
  id: string;
  title: string;
  messages: AIMessage[];
}

export const aiApi = {
  /** Send a message and get the AI response. */
  chat: (data: { conversationId?: string; message: string }): Promise<{
    conversationId: string;
    message: AIMessage;
  }> => apiRequest('/ai/chat', { method: 'POST', body: JSON.stringify(data) }),

  /** List recent conversations. */
  conversations: (): Promise<AIConversation[]> => apiRequest('/ai/conversations'),

  /** Load a conversation with all messages. */
  conversation: (id: string): Promise<AIConversationDetail> => apiRequest(`/ai/conversations/${id}`),

  /** Delete a conversation. */
  deleteConversation: (id: string): Promise<void> =>
    apiRequest(`/ai/conversations/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Task Management
// ---------------------------------------------------------------------------

export interface TaskStats {
  open: number;
  overdue: number;
  dueToday: number;
  dueThisWeek: number;
  completedThisMonth: number;
  activeProjects: number;
  myOpen: number;
  myOverdue: number;
  waiting: number;
}

export interface TaskFilters {
  view?: string;
  status?: string;
  priority?: string;
  category?: string;
  project_id?: string;
  property_id?: string;
  assigned_to?: string;
  due?: string;
  search?: string;
}

export interface TaskDetail {
  task: Task;
  tags: { id: string; name: string }[];
  comments: TaskComment[];
  activity: TaskActivityEntry[];
}

export const tasksApi = {
  list: (filters?: TaskFilters): Promise<Task[]> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    }
    const qs = params.toString();
    return apiRequest(`/tasks${qs ? `?${qs}` : ''}`);
  },

  get: (id: string): Promise<TaskDetail> =>
    apiRequest(`/tasks/${id}`),

  create: (data: Partial<Task>): Promise<Task> =>
    apiRequest('/tasks', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Task>): Promise<Task> =>
    apiRequest(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: string): Promise<void> =>
    apiRequest(`/tasks/${id}`, { method: 'DELETE' }),

  stats: (): Promise<TaskStats> =>
    apiRequest('/tasks/stats'),

  addComment: (taskId: string, body: string): Promise<TaskComment> =>
    apiRequest(`/tasks/${taskId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  setTags: (taskId: string, tags: string[]): Promise<{ id: string; name: string }[]> =>
    apiRequest(`/tasks/${taskId}/tags`, {
      method: 'PUT',
      body: JSON.stringify({ tags }),
    }),
};

export const projectsApi = {
  list: (): Promise<Project[]> =>
    apiRequest('/projects'),

  get: (id: string): Promise<{ project: Project; tasks: Task[] }> =>
    apiRequest(`/projects/${id}`),

  create: (data: Partial<Project>): Promise<Project> =>
    apiRequest('/projects', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Project>): Promise<Project> =>
    apiRequest(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  delete: (id: string): Promise<void> =>
    apiRequest(`/projects/${id}`, { method: 'DELETE' }),
};

export interface MgmtNotification {
  id: string;
  user_id: string;
  type: string;
  category: string;
  priority: string;
  title: string;
  message: string | null;
  entity_type: string | null;
  entity_id: string | null;
  route: string | null;
  is_read: number;
  read_at: number | null;
  created_at: number;
}

export const mgmtNotificationsApi = {
  list: (filter = 'all', page = 1, limit = 20): Promise<{ notifications: MgmtNotification[]; total: number; page: number; limit: number }> =>
    apiRequest(`/mgmt-notifications?filter=${filter}&page=${page}&limit=${limit}`),

  unreadCount: (): Promise<{ count: number }> =>
    apiRequest('/mgmt-notifications/unread-count'),

  markRead: (id: string, isRead = true): Promise<void> =>
    apiRequest(`/mgmt-notifications/${id}/read`, { method: 'PUT', body: JSON.stringify({ is_read: isRead }) }),

  markAllRead: (): Promise<void> =>
    apiRequest('/mgmt-notifications', { method: 'PUT', body: JSON.stringify({ action: 'mark_all_read' }) }),

  dismiss: (id: string): Promise<void> =>
    apiRequest(`/mgmt-notifications/${id}`, { method: 'DELETE' }),

  getPreferences: (): Promise<{ preferences: Record<string, boolean>; categories: string[] }> =>
    apiRequest('/mgmt-notifications/preferences'),

  updatePreferences: (preferences: Record<string, boolean>): Promise<void> =>
    apiRequest('/mgmt-notifications/preferences', { method: 'PUT', body: JSON.stringify({ preferences }) }),
};
